/**
 * The /coming-soon page as a signed-out visitor sees it: how many homes are coming, the sign-in
 * button and blurred PLACEHOLDER cards (no listing data, nothing focusable behind the overlay).
 *
 * Shared by the tenant route's gated grid (its signed-out branch) and the Studio, which shows the
 * page the way most visitors first meet it. The listings themselves stay behind the server-side
 * gate on the live site; nothing here can carry one.
 */
const PLACEHOLDER_CARDS = 6;

export function comingSoonSummary(count: number, signedIn: boolean, area?: string | null): string {
  const noun = count === 1 ? 'home' : 'homes';
  return count === 0
    ? 'Nothing is lined up right now — check back soon.'
    : signedIn
      ? `${count} ${noun} about to hit the market.`
      : `${count} ${noun} coming soon in ${area?.trim() || 'your area'}.`;
}

// The sign-in button is the site's own button (.blk-btn, blocks.css): a hard-coded near-black one
// disappeared on a dark page.
export function ComingSoonTeaser({ count, area }: { count: number; area?: string | null }) {
  const total = Math.max(0, Math.floor(count));
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600" data-testid="coming-soon-count">{comingSoonSummary(total, false, area)}</p>
        <a href="/my" data-testid="coming-soon-signin" className="blk-btn rounded-md px-4 py-2 text-sm font-semibold">
          Sign in to view
        </a>
      </div>

      {total > 0 && (
        <div className="relative">
          {/* Placeholders only: no listing data, and nothing focusable behind the overlay. */}
          <div
            data-testid="coming-soon-placeholders"
            data-blurred="true"
            aria-hidden="true"
            className="auto-list-grid pointer-events-none grid select-none grid-cols-1 gap-6 blur-md sm:grid-cols-2 lg:grid-cols-3"
          >
            {Array.from({ length: Math.min(total, PLACEHOLDER_CARDS) }, (_, i) => (
              <div key={i} className="auto-list-card blk-card overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="aspect-[4/3] w-full bg-gray-200" />
                <div className="space-y-2 p-4">
                  <div className="h-5 w-1/2 rounded bg-gray-300" />
                  <div className="h-4 w-3/4 rounded bg-gray-200" />
                  <div className="h-4 w-1/3 rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-start justify-center pt-16">
            <a href="/my" data-testid="coming-soon-overlay" className="rounded-xl bg-white/95 px-6 py-4 text-center shadow-xl ring-1 ring-gray-200">
              <span className="block text-base font-bold text-gray-900">Sign in to view coming-soon homes</span>
              <span className="mt-1 block text-sm text-gray-600">Free account — we email you a link, no password.</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
